/**
 * WebABVariantEditor Component
 *
 * Range slider variant list for Web A/B test weight distribution.
 * Each variant references a published WebPlacement (not embedded config).
 *
 * Key behavior:
 * - Variants reference existing published WebPlacements
 * - Simpler picker (just select placement, no onboarding+version)
 * - No sorting needed (variants order determines evaluation)
 *
 * Requirements:
 * - 2-10 variants with weights summing to 100%
 * - At least one variant must have weight > 0
 * - All variants must reference published placements
 */

import React, { useCallback, useMemo, useState, useEffect, useLayoutEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Slider,
  Alert,
  Paper,
  Chip,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { WebABVariant, WebABVariantRole, WebPlacement } from '@modules/webplacements/types/webplacements.types';
import {
  WEB_AB_VALIDATION,
  WEB_AB_VARIANT_ROLES,
  getDefaultWebABVariantRole,
} from '@modules/webplacements/types/webplacements.types';
import { distributeWeightsEvenly, MIN_WEIGHT } from '@modules/placements/utils/weightDistribution';

export interface WebABVariantEditorProps {
  /** Current variants array */
  variants: WebABVariant[];
  /** Callback when variants change */
  onChange: (variants: WebABVariant[]) => void;
  /** Available published placements for selection */
  availablePlacements: WebPlacement[];
  /** Whether placements are loading */
  placementsLoading?: boolean;
  /** Error message to display */
  error?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Callback to open placement picker */
  onOpenPicker: (variantId: string) => void;
}

/**
 * Generate the next variant letter (A, B, C, ..., Z, AA, AB, etc.)
 */
const getNextVariantLetter = (variants: WebABVariant[]): string => {
  const existingLetters = new Set(variants.map((v) => v.id));
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  // Try single letters first
  for (const letter of letters) {
    if (!existingLetters.has(letter)) {
      return letter;
    }
  }

  // Try double letters
  for (const first of letters) {
    for (const second of letters) {
      const combo = `${first}${second}`;
      if (!existingLetters.has(combo)) {
        return combo;
      }
    }
  }

  return `V${variants.length + 1}`;
};

/**
 * Calculate range boundaries from weights
 * Returns array of [start, end] for each variant
 */
const calculateRanges = (variants: WebABVariant[]): Array<[number, number]> => {
  const ranges: Array<[number, number]> = [];
  let currentStart = 0;

  for (const variant of variants) {
    const end = currentStart + variant.weight;
    ranges.push([currentStart, end]);
    currentStart = end;
  }

  return ranges;
};

const parseTags = (value: string): string[] =>
  value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

const formatRoleLabel = (role: WebABVariantRole): string =>
  role.charAt(0).toUpperCase() + role.slice(1);

/**
 * Internal component for range slider with local state.
 */
interface VariantRangeSliderProps {
  value: [number, number];
  onCommit: (value: number[]) => void;
  disabled: boolean;
  label: string;
}

const VariantRangeSlider: React.FC<VariantRangeSliderProps> = ({
  value,
  onCommit,
  disabled,
  label,
}) => {
  const [localValue, setLocalValue] = useState<[number, number]>(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <Slider
      aria-label={label}
      value={localValue}
      onChange={(_, v) => setLocalValue(v as [number, number])}
      onChangeCommitted={(_, v) => onCommit(v as number[])}
      min={0}
      max={100}
      disabled={disabled}
      valueLabelDisplay="auto"
      disableSwap
    />
  );
};

/**
 * Internal component for variant name input with local state.
 */
interface VariantNameInputProps {
  value: string;
  onChange: (name: string) => void;
  disabled: boolean;
}

const VariantNameInput = React.memo<VariantNameInputProps>(function VariantNameInput({
  value,
  onChange,
  disabled,
}) {
  const [localName, setLocalName] = useState(value);

  useLayoutEffect(() => {
    setLocalName(value);
  }, [value]);

  return (
    <TextField
      label="Variant Name"
      size="small"
      value={localName}
      onChange={(e) => setLocalName(e.target.value)}
      onBlur={() => {
        if (localName !== value) {
          onChange(localName);
        }
      }}
      disabled={disabled}
      sx={{ flexGrow: 1, mr: 1 }}
    />
  );
});

/**
 * WebABVariantEditor Component
 */
export const WebABVariantEditor: React.FC<WebABVariantEditorProps> = ({
  variants,
  onChange,
  availablePlacements,
  placementsLoading = false,
  error,
  disabled = false,
  onOpenPicker,
}) => {
  const theme = useTheme();

  // Variants without placement selected
  const variantsWithoutPlacement = useMemo(() => {
    return variants.filter((v) => !v.webPlacementId);
  }, [variants]);

  // Check for duplicate placement selections
  const duplicates = useMemo(() => {
    const seen = new Map<string, string[]>();
    variants.forEach((v) => {
      if (v.webPlacementId) {
        if (!seen.has(v.webPlacementId)) seen.set(v.webPlacementId, []);
        seen.get(v.webPlacementId)!.push(v.name);
      }
    });
    return Array.from(seen.entries())
      .filter(([, names]) => names.length > 1)
      .map(([, names]) => names);
  }, [variants]);

  const hasDuplicates = duplicates.length > 0;

  // Calculate ranges for all variants
  const ranges = useMemo(() => calculateRanges(variants), [variants]);

  const canAddMore = variants.length < WEB_AB_VALIDATION.VARIANT_MAX_COUNT;
  const canDelete = variants.length > WEB_AB_VALIDATION.VARIANT_MIN_COUNT;

  // Add new variant
  const handleAddVariant = useCallback(() => {
    if (!canAddMore) return;

    const letter = getNextVariantLetter(variants);
    const newVariant: WebABVariant = {
      id: letter,
      name: `Variant ${letter}`,
      role: getDefaultWebABVariantRole(variants.length),
      weight: 0,
      webPlacementId: '',
      webPlacementAlias: '',
      webPlacementName: '',
    };

    // Get new weights distribution
    const newWeights = distributeWeightsEvenly(variants.length + 1);

    // Apply new weights
    const updatedVariants = variants.map((v, i) => ({
      ...v,
      weight: newWeights[i],
    }));
    updatedVariants.push({
      ...newVariant,
      weight: newWeights[variants.length],
    });

    onChange(updatedVariants);
  }, [variants, canAddMore, onChange]);

  // Remove variant
  const handleRemoveVariant = useCallback(
    (id: string) => {
      if (!canDelete) return;

      const remainingVariants = variants.filter((v) => v.id !== id);
      const newWeights = distributeWeightsEvenly(remainingVariants.length);
      const updatedVariants = remainingVariants.map((v, i) => ({
        ...v,
        weight: newWeights[i],
      }));

      onChange(updatedVariants);
    },
    [variants, canDelete, onChange]
  );

  // Update variant field
  const handleUpdateVariant = useCallback(
    (id: string, updates: Partial<WebABVariant>) => {
      onChange(
        variants.map((v) => (v.id === id ? { ...v, ...updates } : v))
      );
    },
    [variants, onChange]
  );

  // Handle range slider change
  const handleRangeChange = useCallback(
    (index: number, newRange: number[]) => {
      const [newStart, newEnd] = newRange;
      const currentRange = ranges[index];
      const isFirst = index === 0;
      const isLast = index === variants.length - 1;

      const newVariants = variants.map((v) => ({ ...v }));

      const startChanged = newStart !== currentRange[0];
      const endChanged = newEnd !== currentRange[1];

      if (startChanged && !isFirst) {
        const prevIndex = index - 1;
        const prevStart = ranges[prevIndex][0];
        const clampedStart = Math.max(prevStart + MIN_WEIGHT, Math.min(newStart, currentRange[1] - MIN_WEIGHT));

        newVariants[prevIndex].weight = clampedStart - prevStart;
        newVariants[index].weight = currentRange[1] - clampedStart;
      }

      if (endChanged && !isLast) {
        const nextIndex = index + 1;
        const nextEnd = ranges[nextIndex][1];
        const clampedEnd = Math.max(currentRange[0] + MIN_WEIGHT, Math.min(newEnd, nextEnd - MIN_WEIGHT));

        newVariants[index].weight = clampedEnd - currentRange[0];
        newVariants[nextIndex].weight = nextEnd - clampedEnd;
      }

      if (isFirst && startChanged) return;
      if (isLast && endChanged) return;

      onChange(newVariants);
    },
    [variants, ranges, onChange]
  );

  return (
    <Box>
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Duplicate warning */}
      {hasDuplicates && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Same placement selected for: {duplicates.map((names) => names.join(' & ')).join('; ')}
        </Alert>
      )}

      {/* Missing placement info */}
      {variantsWithoutPlacement.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select placement for: {variantsWithoutPlacement.map((v) => v.name).join(', ')}
        </Alert>
      )}

      {/* No published placements warning */}
      {!placementsLoading && availablePlacements.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No published placements available. Publish at least 2 web placements to create an A/B test.
        </Alert>
      )}

      {/* Variants List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {variants.map((variant, index) => (
          <Paper
            key={variant.id}
            variant="outlined"
            data-testid={`webabt-variant-${variant.id}`}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {/* Top row: Name and Delete */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <VariantNameInput
                value={variant.name}
                onChange={(name) => handleUpdateVariant(variant.id, { name })}
                disabled={disabled}
              />
              <IconButton
                aria-label="delete"
                onClick={() => handleRemoveVariant(variant.id)}
                disabled={disabled || !canDelete}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* Variant metadata */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
                gap: 1.5,
              }}
            >
              <TextField
                select
                size="small"
                label="Role"
                value={variant.role || getDefaultWebABVariantRole(index)}
                onChange={(event) => handleUpdateVariant(variant.id, { role: event.target.value as WebABVariantRole })}
                disabled={disabled}
                data-testid={`webabt-variant-role-${variant.id}`}
              >
                {WEB_AB_VARIANT_ROLES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {formatRoleLabel(role)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                label="Variant Description"
                value={variant.description || ''}
                onChange={(event) => handleUpdateVariant(variant.id, { description: event.target.value })}
                disabled={disabled}
                data-testid={`webabt-variant-description-${variant.id}`}
                multiline
                minRows={1}
                helperText="What this variant changes and what it measures"
              />
            </Box>

            <TextField
              size="small"
              label="Variant Tags"
              value={variant.tags?.join(', ') || ''}
              onChange={(event) => handleUpdateVariant(variant.id, { tags: parseTags(event.target.value) })}
              disabled={disabled}
              data-testid={`webabt-variant-tags-${variant.id}`}
              helperText="Comma-separated tags"
            />

            {/* Range Slider */}
            <Box sx={{ px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Range: {ranges[index]?.[0] ?? 0} - {ranges[index]?.[1] ?? 100} ({variant.weight}%)
              </Typography>
              <VariantRangeSlider
                label={`Range for ${variant.name}`}
                value={ranges[index] ?? [0, 100]}
                onCommit={(value) => handleRangeChange(index, value)}
                disabled={disabled}
              />
            </Box>

            {/* Placement Selection */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Web Placement
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  data-testid={`webabt-variant-select-${variant.id}`}
                  onClick={() => onOpenPicker(variant.id)}
                  disabled={disabled || placementsLoading}
                  sx={{ flexGrow: 1, justifyContent: 'flex-start' }}
                >
                  {variant.webPlacementId ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" noWrap>
                        {variant.webPlacementName}
                      </Typography>
                      <Chip
                        label={`/${variant.webPlacementAlias}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      Select placement...
                    </Typography>
                  )}
                </Button>
                {variant.webPlacementAlias && (
                  <IconButton
                    size="small"
                    href={`/${variant.webPlacementAlias}`}
                    target="_blank"
                    disabled={!variant.webPlacementAlias}
                    title="Open in new tab"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Add Variant / Max Reached */}
      <Box sx={{ mt: 2 }}>
        {canAddMore ? (
          <Button
            variant="outlined"
            data-testid="webabt-add-variant"
            startIcon={<AddIcon />}
            onClick={handleAddVariant}
            disabled={disabled}
          >
            Add Variant
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Maximum of {WEB_AB_VALIDATION.VARIANT_MAX_COUNT} variants reached
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default WebABVariantEditor;
